import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as authApi from '../api/endpoints/auth';
import { getStoredTokens, storeTokens, clearTokens, setOnSessionExpired } from '../api/client';
import { TOKEN_STORAGE_KEYS } from '../config/api';
import { clearRecentSearches } from '../utils/recentSearches';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [customer, setCustomerState] = useState(null);
  const [authStatus, setAuthStatus] = useState('loading');
  const sessionRevisionRef = useRef(0);

  useEffect(() => {
    setOnSessionExpired(() => {
      sessionRevisionRef.current += 1;
      clearTokens();
      clearRecentSearches();
      setCustomerState(null);
      setAuthStatus('guest');
    });
    return () => setOnSessionExpired(null);
  }, []);

  // A token in storage is only a session candidate. Keep protected routes in
  // their loading state until /me validates it (including one refresh retry),
  // otherwise a stale token can mount authenticated pages indefinitely.
  useEffect(() => {
    const { accessToken } = getStoredTokens();
    if (!accessToken) {
      setAuthStatus('guest');
      return;
    }

    const revision = sessionRevisionRef.current;
    Promise.resolve()
      .then(() => authApi.getMe())
      .then((response) => {
        if (revision !== sessionRevisionRef.current) return;
        setCustomerState(response.data);
        setAuthStatus('authenticated');
      })
      .catch((err) => {
        if (revision !== sessionRevisionRef.current) return;
        if (err?.code === 'SESSION_EXPIRED' || err?.status === 401 || err?.status === 403) {
          sessionRevisionRef.current += 1;
          clearTokens();
          clearRecentSearches();
        }
        setCustomerState(null);
        setAuthStatus('guest');
      });
  }, []);

  // Cross-tab sync: this tab only learns about a 401 via its own API calls,
  // so a tab sitting idle after logout (or token clearing) elsewhere never
  // hears about it until it happens to make a request. The storage event
  // fires in every OTHER tab when localStorage changes, so use it to mirror
  // a cleared access token into this tab's auth state immediately.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== TOKEN_STORAGE_KEYS.access) return;
      if (event.newValue) return;
      sessionRevisionRef.current += 1;
      clearRecentSearches();
      setCustomerState(null);
      setAuthStatus('guest');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await authApi.login(email, password);
    const { accessToken, refreshToken, customer: loggedInCustomer } = response.data;
    sessionRevisionRef.current += 1;
    storeTokens({ accessToken, refreshToken });
    setCustomerState(loggedInCustomer);
    setAuthStatus('authenticated');
    return loggedInCustomer;
  }, []);

  const register = useCallback(async (payload) => {
    const response = await authApi.register(payload);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    // Local logout must be immediate and can't be held up by the network —
    // clear credentials first, then revoke the refresh token server-side as
    // a best-effort background call so a slow/hanging request never blocks
    // the UI from signing the user out.
    const { refreshToken } = getStoredTokens();
    sessionRevisionRef.current += 1;
    clearTokens();
    clearRecentSearches();
    setCustomerState(null);
    setAuthStatus('guest');
    if (refreshToken) {
      authApi.logout(refreshToken).catch(() => {
        // Best-effort — the token is already discarded locally either way.
      });
    }
  }, []);

  const refreshCustomer = useCallback(async () => {
    const response = await authApi.getMe();
    setCustomerState(response.data);
    return response.data;
  }, []);

  const setCustomer = useCallback((patch) => {
    setCustomerState((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const value = useMemo(
    () => ({ customer, authStatus, login, register, logout, refreshCustomer, setCustomer }),
    [customer, authStatus, login, register, logout, refreshCustomer, setCustomer]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
